import axios from "axios";

// API Base URLs
export const BASE_URL = "https://api.happileehotelfolio.com/api";
export const BASE_URL_for_images = "https://api.happileehotelfolio.com";

// export const BASE_URL = "http://localhost:8001/api";
// export const BASE_URL_for_images = "http://localhost:8001";

// Create an axios instance
const api = axios.create({
  baseURL: BASE_URL,
});

// Add request interceptor for debugging (optional - can be removed in production)
api.interceptors.request.use(
  (config) => {
    console.log('🚀 API Request:', {
      url: config.url,
      method: config.method,
      fullURL: `${config.baseURL}${config.url}`,
      data: config.data instanceof FormData ? 'FormData' : config.data
    });
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging (optional - can be removed in production)
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data, // ← ADD THIS
    });
    return response;
  },
  (error) => {
    console.error('❌ API Error Response:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
    });
    
    // ⭐ ADD THIS - It will show the full error message
    if (error.response?.data) {
      console.error('❌ FULL BACKEND ERROR:', JSON.stringify(error.response.data, null, 2));
    }
    
    return Promise.reject(error);
  }
);

// Function to get stored token from localStorage
export const getToken = (): string | null => {    
  return localStorage.getItem("token");
};

// Function to logout and clear localStorage
export const logoutAPI = (): void => {
  localStorage.removeItem("user");
  localStorage.removeItem("token");
};

export const getUser = (): any => {
  const user = localStorage.getItem("user");
  if (!user || user === "undefined") {
    logoutAPI();
    window.location.href = "/login";
    return null;
  }
  try {
    return JSON.parse(user);
  } catch (error) {
    logoutAPI();
    window.location.href = "/login";
    return null;
  }
};

// Generalized function for making authenticated API calls
const apiRequest = async (
  endpoint: string,
  data: any,
  method: "post" | "get" | "put" = "post",
  config: any = {}
): Promise<any> => {
  try {
    const token = getToken();

    // Set the Authorization header if token exists
    const headers: Record<string, string> = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    // Don't set Content-Type for FormData - let axios handle it
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Make the API call
    const response = await api.request({
      url: endpoint,
      method,
      data,
      headers,
      ...config,
    });

    return response.data;
  } catch (error) {
    // Handle the error appropriately
    throw error;
  }
};

// Function for login API and storing data in localStorage
export const loginAPI = async (payload: any): Promise<any> => {
  console.log('🔐 Login API called');
  
  // Validate payload
  if (!payload.email || !payload.password) {
    throw new Error('Email and password are required');
  }

  const data = await apiRequest("/users/login", payload);

  if (data?.token) {
    localStorage.setItem("user", JSON.stringify(data.userDetails));
    localStorage.setItem("token", data.token);
    console.log('✅ Login successful - token stored');
  }

  return data;
};

export const EmployeesLoginAPI = async (payload: any): Promise<any> => {
  const data = await apiRequest("/Employees/login", payload, "post");

  if (data?.token) {
    localStorage.setItem("user", JSON.stringify(data.employeeDetails));
    localStorage.setItem("token", data.token);
  }
  return data;
};

// Register Hotel And Hotel Owner
export const registerHotelAndHotelOwner = async (payload: any): Promise<any> => {
  const data = await apiRequest("/hotel/createHotel", payload, "post");
  return data;
};

// Get all hotels which status is pending
export const getPendingHotels = async (payload: any): Promise<any> => {
  const data = await apiRequest("/hotel/pending", payload, "get");
  return data;
};

// Get all hotels which status is approved
export const getApprovedHotels = async (page = 1, pageSize = 10, searchQuery = ''): Promise<any> => {
  const searchParam = searchQuery ? `&searchQuery=${encodeURIComponent(searchQuery)}` : '';
  const data = await apiRequest(`/hotel/approved?page=${page}&pageSize=${pageSize}${searchParam}`, null, "get");
  return data;
};

export const getHotelDetailsAPI = (userData: any): Promise<any> => {
  return apiRequest("/hotel/getHotelDetails", userData);
};

// Get all hotels which status is rejected
export const getRejectedHotels = async (page = 1, pageSize = 10, searchQuery = ''): Promise<any> => {
  const searchParam = searchQuery ? `&searchQuery=${encodeURIComponent(searchQuery)}` : '';
  const data = await apiRequest(`/hotel/rejected?page=${page}&pageSize=${pageSize}${searchParam}`, null, "get");
  return data;
};

// approval status of the hotel
export const getApprovalStatus = async (hotelId: any, status: any): Promise<any> => {
  const data = await apiRequest(`/hotel/adminApproveHotel/${hotelId}`, { status }, 'post');
  return data;
};

export const registerUserAPI = (userData: any): Promise<any> => {
  return apiRequest("/users/register", userData);
};

export const getHotels = (userData: any): Promise<any> => {
  return apiRequest("/hotel/getHotels", userData, "get");
};

export const createHotel = (userData: any): Promise<any> => {
  return apiRequest("/hotel/createHotel", userData);
};

export const updateHotel = ({ formData, id }: { formData: any; id: any }): Promise<any> => {
  return apiRequest(`/hotel/updateHotel/${id}`, formData);
};

export const getAllEmployeesByCreatedBy = (userData: any): Promise<any> => {
  return apiRequest("/Employees/getAllEmployeesByCreatedBy", userData);
};

export const registerUser = (userData: any): Promise<any> => {
  return apiRequest("/Employees/createEmployee", userData);
};

export const getEmployeeById = (EmployeeID: string, userData: any): Promise<any> => {
  return apiRequest(`/Employees/getEmployeeById/${EmployeeID}`, userData, "get");
};

export const updateUser = (EmployeeID: string, userData: any): Promise<any> => {
  return apiRequest(`/Employees/updateEmployee/${EmployeeID}`, userData);
};

export const getHotelById = (userData: any): Promise<any> => {
  return apiRequest("/hotel/getHotelById", userData);
};

// Salary api
export const addSalaryAPI = (userData: any): Promise<any> => {
  return apiRequest("/Salary/createSalary", userData);
};

export const getAllSalaryAPI = (userData: any): Promise<any> => {
  return apiRequest("/Salary/getAllSalary", userData, "get");
};

export const getEmployeeMonthlySalaryReportAPI = (userData: any): Promise<any> => {
  return apiRequest("/Salary/getEmployeeMonthlySalaryReport", userData);
};

export const updateSalaryAPI = (userData: any): Promise<any> => {
  return apiRequest("/Salary/updateSalary", userData);
};

export const updateAdvanceSalaryGiven = (userData: any): Promise<any> => {
  return apiRequest("/Salary/updateAdvanceSalaryGiven", userData);
};

export const getSalarybyid = (EmployeeID: string, userData: any): Promise<any> => {
  return apiRequest(`/Salary/getAllSalariesByEmployeeId/${EmployeeID}`, userData, "get");
};

export const addmenuAPI = (userData: any): Promise<any> => {
  return apiRequest("/Hotelmenu/createMenu", userData);
};

export const getAllmenuAPI = (userData: any): Promise<any> => {
  return apiRequest("/Hotelmenu/getAllMenuItems", userData, "get");
};

export const updatemenuAPI = (id: string, userData: any): Promise<any> => {
  return apiRequest(`/Hotelmenu/updateMenu/${id}`, userData);
};

export const deletemenuitemAPI = (id: string, userData: any): Promise<any> => {
  return apiRequest(`/Hotelmenu/DeleteMenu/${id}`, userData);
};

export const getAllguestfiedsAPI = (userData: any): Promise<any> => {
  return apiRequest("/guests/getallguests", userData);
};

export const addcustomerAPI = (userData: any): Promise<any> => {
  return apiRequest("/Customer/addCustomer", userData);
};

// In api.ts — fix the payload:
export const getAllCustomerAPI = (userData: any): Promise<any> => {
  // ✅ Map pageSize → limit and currentPage → page
  const { hotel_id, currentPage, pageSize, startDate, endDate } = userData;
  return apiRequest("/Customer/getAllCustomers", {
    hotel_id,
    page: currentPage,      // ✅ controller reads req.query.page
    limit: pageSize,        // ✅ controller reads req.query.limit
    startDate,
    endDate,
  });
};

export const addMenuItemsForCustomerAPI = (userData: any): Promise<any> => {
  return apiRequest("/Customermenu/addMenuItemsForCustomer", userData);
};

export const getCustomerMenuByCustomerIdAPI = (id: string, userData?: any): Promise<any> => {
  return apiRequest(`/Customermenu/getMenuDetailsByCustomerId/${id}`, userData, "get");
};

export const gethotelinfoIdAPI = (id: string, userData?: any): Promise<any> => {
  return apiRequest(`/hotel/hotelByIdinfo/${id}`, userData, "get");
};

export const getHotelInsightsAPI = (hotel_id: string, filters?: any): Promise<any> => {
  const requestData = { hotel_id, ...filters };
  return apiRequest('/hotelInsights/getInsights', requestData, 'post');
};

// reservations api 
export const addReservationAPI = (userData: any): Promise<any> => {
  return apiRequest("/reservations/addreservations", userData);
};

export const getHotelReservations = (hotel_id: any): Promise<any> => {
  return apiRequest("/reservations/gethotelreservations", { hotel_id }, "post");
};

export const checkinReservation = (reservationId: string): Promise<any> => {
  return apiRequest(`/reservations/checkin/${reservationId}`, {}, 'post');
};

export const updatecheckintimeAPI = (reservationData: any): Promise<any> => {
  return apiRequest('/reservations/update-checkin-time', reservationData, 'post');
};

export const updatecheckouttimeAPI = (reservationData: any): Promise<any> => {
  return apiRequest('/reservations/update-checkout-time', reservationData, 'post');
};

export const getAllReservationsdashboard = (params: any): Promise<any> => {
  return apiRequest("/reservations/dashboard", params, "post");
};

export const getHotelBillings = (params: any): Promise<any> => {
  const { hotel_id, page = 1, limit = 1000 } = params;
  return apiRequest(`/billing/getHotelBillings?page=${page}&limit=${limit}`, { hotel_id }, "post");
};

export const getReservationById = (reservationId: any, formData: any): Promise<any> => {
  return apiRequest(`/reservations/reservations/${reservationId}`, formData);
};

export const updateReservation = (hotel_id: any, formData: any): Promise<any> => {
  return apiRequest(`/reservations/updatereservations/${hotel_id}`, formData);
};

export const getReservationDetails = (reservationId: string): Promise<any> => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  let hotel_id;
  if (user?.role === "RECEPTIONIST") {
    hotel_id = user?.hotel?.hotel_id;
  } else {
    hotel_id = user?.hotel_id;
  }

  const url = `/reservations/reservations/${reservationId}`;
  // Some accounts are refused (403) when a hotel_id is sent; the server can work it
  // out from the login token, so retry once without it before giving up.
  return apiRequest(url, { hotel_id }, 'post').catch((error: any) => {
    if (error?.response?.status === 403) return apiRequest(url, {}, 'post');
    throw error;
  });
};

// Add checkout reservation API
export const checkoutReservation = (reservationId: string, checkoutDetails: any): Promise<any> => {
  return apiRequest(`/reservations/checkout/${reservationId}`, checkoutDetails, 'post');
};

export const getInvoiceData = (id: any, formData: any): Promise<any> => {
  return apiRequest(`/invoice/${id}`, formData, 'get');
};

// Room api
export const GetAllRooms = (userData: any): Promise<any> => {
  return apiRequest("/room/getAllRooms", userData);
};

export const getAllRoomsWithoutPagination = (userData: any): Promise<any> => {
  return apiRequest("/room/getAllRoomsWithoutPagination", userData);
};

export const AddRoom = (formDataToSend: any): Promise<any> => {
  return apiRequest("/room/createRoom", formDataToSend);
};

export const UpdateRoom = (id: any, formDataToSend: any): Promise<any> => {
  return apiRequest(`/room/updateRoom/${id}`, formDataToSend);
};

// guest api
export const getAllGuests = (userData: any): Promise<any> => {
  return apiRequest("/guests/getAllGuests", userData);
};

export const addGuest = (userData: any): Promise<any> => {
  return apiRequest("/guests/addguests", userData);
};

export const getGuestById = (id: any, formDataToSend: any): Promise<any> => {
  return apiRequest(`/guests/getguests/${id}`, formDataToSend, "post");
};

export const updateGuest = (id: any, formDataToSend: any): Promise<any> => {
  return apiRequest(`/guests/updateguests/${id}`, formDataToSend);
};

export const deleteGuest = (id: any, formDataToSend: any): Promise<any> => {
  return apiRequest(`/guests/deleteguests/${id}`, formDataToSend);
};

export const getByMobileNumber = (mobileNumber: any, formDataToSend: any): Promise<any> => {
  return apiRequest(`/guests/guest/${mobileNumber}`, formDataToSend, "get");
};

// Customer api
export const deleteCustomerAPI = (id: string, userData: any): Promise<any> => {
  return apiRequest(`/Customer/deleteCustomer/${id}`, userData);
};

export const updateMenuItemsForCustomerAPI = (customerId: any, userData: any): Promise<any> => {
  // ✅ CRITICAL: Explicitly specify 'post' method (was defaulting to 'post' anyway, but make it explicit)
  return apiRequest(`/Customermenu/updateMenuItemsForCustomer/${customerId}`, userData, 'post');
};

export const deletemenuitemcustomerAPI = (
  customerId: string, 
  menuId: string, 
  reservationId: number | null = null,  // ✅ NEW PARAMETER (third parameter)
  userData: any = {}
): Promise<any> => {
  // Build query string with reservation_id
  const queryParams = reservationId !== null 
    ? `?reservation_id=${reservationId}` 
    : '';
  
  console.log(`🗑️ DELETE API: customer=${customerId}, menu=${menuId}, reservation=${reservationId}`);
  
  return apiRequest(
    `/Customermenu/menuitem/${customerId}/deleteMenuItems/${menuId}${queryParams}`, 
    userData,
    'post'  // Ensure correct HTTP method
  );
};


export const applyRestaurantBillingAPI = async (
  customerId: number | string,
  payload: {
  discount: number;
  gst_overrides: Record<string, number>;
  billing_status: string;
  reservation_id?: number | null;
  billing_type?: "DIRECT" | "ROOM";
}
) => {
  const token = localStorage.getItem("token");
  
  // ✅ BASE_URL is already "http://localhost:8001/api"
  // Router is mounted at /Customer, route is /:customer_id/apply-billing
  // So full path = BASE_URL + /Customer/18/apply-billing
  const response = await fetch(
    `${BASE_URL}/Customer/${customerId}/apply-billing`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const text = await response.text();
    console.error("API error:", text);
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
};
 


export const getCustomerbillAPI = (customer_id: string, userData?: any): Promise<any> => {
  return apiRequest(`/Customermenu/getMenuDetailsbill/${customer_id}`, userData, "get");
};

// spa Routes
export const addSpaService = (userData: any): Promise<any> => {
  return apiRequest("/spaService/addservice", userData);
};

export const getSpaService = (hotel_id: any): Promise<any> => {
  return apiRequest("/spaService/services", hotel_id);
};

export const updateSpaService = (id: any, formData: any): Promise<any> => {
  return apiRequest(`spaService/updateservice/${id}`, formData, 'post');
};

export const deleteSpaService = (id: string): Promise<any> => {
  return apiRequest(`spaService/deleteservice/${id}`, {}, 'post');
};

export const addSpaAppointment = (userData: any): Promise<any> => {
  return apiRequest("/spaAppointment/addappointment", userData);
};

export const updateSpaAppointment = (userData: any): Promise<any> => {
  return apiRequest(`/spaAppointment/updateappointment/${userData.id}`, userData);
};

export const getSpaAppointment = (userData: any): Promise<any> => {
  return apiRequest("/spaAppointment/appointments", userData);
};

export const deleteSpaAppointment = (userData: any): Promise<any> => {
  return apiRequest(`/spaAppointment/deleteappointment/${userData.id}`, userData);
};

// Room History APIs
export const getRoomHistory = (roomId: any, page = 1, pageSize = 10): Promise<any> => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  let hotel_id;
  if (user?.role === "RECEPTIONIST") {
    hotel_id = user?.hotel?.hotel_id;
  } else {
    hotel_id = user?.hotel_id;
  }
  
  return apiRequest(`/roomHistory/room/${roomId}`, { 
    hotel_id,
    page,
    pageSize
  }, 'get');
};

export const getRoomHistoryByDateRange = (
  roomId: any, 
  startDate: any, 
  endDate: any, 
  page = 1, 
  pageSize = 10
): Promise<any> => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  let hotel_id;
  if (user?.role === "RECEPTIONIST") {
    hotel_id = user?.hotel?.hotel_id;
  } else {
    hotel_id = user?.hotel_id;
  }

  return apiRequest(`/roomHistory/dateRange`, {
    roomId,
    startDate,
    endDate,
    hotel_id,
    page,
    pageSize
  }, 'get');
};

export const getAllRoomHistory = (page = 1, pageSize = 10): Promise<any> => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  let hotel_id;
  if (user?.role === "RECEPTIONIST") {
    hotel_id = user?.hotel?.hotel_id;
  } else {
    hotel_id = user?.hotel_id;
  }

  return apiRequest(`/roomHistory/getAllHistory`, {
    page,
    pageSize,
    hotel_id
  }, 'get');
};

export const fetchReservationDetails = async (reservationId: any): Promise<any> => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const hotel_id =
    user?.role === "RECEPTIONIST" ? user?.hotel?.hotel_id : user?.hotel_id;

  const url = `/reservations/reservations/${reservationId}`;
  return apiRequest(url, { hotel_id }, "post").catch((error: any) => {
    if (error?.response?.status === 403) return apiRequest(url, {}, "post");
    throw error;
  });
};


// billing APIs
export const createBilling = (billingData: any): Promise<any> => {
  return apiRequest("/billing/createBilling", billingData);
};

export const getBillingById = (id: any): Promise<any> => {
  return apiRequest(`/billing/getBillingById/${id}`, null, "get");
};

export const createFoodBilling = (billingData: any): Promise<any> => {
  return apiRequest("food-billing/createFoodBilling", billingData, "post");
};

export const updateFoodBilling = (id: any, billingData: any): Promise<any> => {
  return apiRequest(`food-billing/${id}`, billingData, "put");
};

export const getFoodBillingById = (customer_id: any): Promise<any> => {
  return apiRequest(`food-billing/getCustomerFoodBillings/${customer_id}`, null, "get");
};

export const markFoodBillingsAsPaidAPI = (billingData: any): Promise<any> => {
  return apiRequest("/Customer/markFoodBillingsAsPaid", billingData, "post");
};

export const getCheckoutFoodCharges = async (customerId: number, reservationId?: number) => {
  try {
    const timestamp = Date.now();
    const queryParams = reservationId 
      ? `?reservation_id=${reservationId}&_t=${timestamp}` 
      : `?_t=${timestamp}`;
    
    const response = await fetch(
      `${BASE_URL}/Customermenu/checkout-food-charges/${customerId}${queryParams}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch checkout food charges');
    }

    const data = await response.json();
    return data;

  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed'
    };
  }
};



export const updateCustomerBillingStatusAPI = (customerId: number, billingData: any): Promise<any> => {
  return apiRequest(`/Customer/updateBillingStatus/${customerId}`, billingData, "post");
};


export const createSpaBilling = (billingData: any): Promise<any> => {
  return apiRequest("spa-billing/spaBilling", billingData);
};

// export const getSpaBillingById = (customer_id: any): Promise<any> => {
//   return apiRequest(`spa-billing/spaBilling/${customer_id}`, null, "get");
// };

export const getSpaBillingByAppointment = (appointmentId: number): Promise<any> => {
  return apiRequest(`/spa-billing/by-appointment/${appointmentId}`, null, "get");
};


export const updateSpaBilling = (id: any, billingData: any): Promise<any> => {
  return apiRequest(`spa-billing/updateSpaBilling/${id}`, billingData, "post");
};

export const updatependingamount = (userData: any): Promise<any> => {
  return apiRequest("/billing/updatePendingPayment", userData);
};

// New API functions for reservation context
export const getGuestReservations = (guestId: any, hotelId: any): Promise<any> => {
  return apiRequest(`/reservations/guest/${guestId}`, { hotel_id: hotelId }, "post");
};

export const getActiveReservations = (hotelId: any): Promise<any> => {
  return apiRequest(`/reservations/active`, { hotel_id: hotelId }, "post");
};

// Enhanced spa appointment with reservation context
export const addSpaAppointmentWithReservation = (userData: any): Promise<any> => {
  return apiRequest("/spaAppointment/addappointment", userData);
};

// Enhanced food billing with reservation context  
export const createFoodBillingWithReservation = (billingData: any): Promise<any> => {
  return apiRequest("food-billing/createFoodBilling", billingData, "post");
};

// Sub-Admin creation API
export const createSubAdminAPI = (userData: any): Promise<any> => {
  return apiRequest("/users/createUser", userData, "post");
};

// Get all users API
export const getAllUsersAPI = (userData: any): Promise<any> => {
  return apiRequest("/users/getAll", userData, "post");
};

// Update user profile API
export const updateUserProfileAPI = (userData: any): Promise<any> => {
  return apiRequest("/users/update", userData, "post");
};

// Contact Us API functions
export const createContactMessageAPI = (contactData: any): Promise<any> => {
  return apiRequest("/contactus/create", contactData, "post");
};

export const getAllContactMessagesAPI = (params: any): Promise<any> => {
  return apiRequest("/contactus/getAll", params, "post");
};

export const getContactMessageByIdAPI = (id: any): Promise<any> => {
  return apiRequest(`/contactus/getById/${id}`, null, "get");
};

export const getContactMessagesByStatusAPI = (status: any, params: any): Promise<any> => {
  return apiRequest(`/contactus/getByStatus/${status}`, params, "post");
};

export const updateContactMessageStatusAPI = (id: any, status: any): Promise<any> => {
  return apiRequest(`/contactus/updateStatus/${id}`, { status });
};

export const updateContactMessageAPI = (id: any, contactData: any): Promise<any> => {
  return apiRequest(`/contactus/update/${id}`, contactData);
};

export const deleteContactMessageAPI = (id: any): Promise<any> => {
  return apiRequest(`/contactus/delete/${id}`, null);
};

export const exportappointmentsbydaterangeAPI = (userData: any): Promise<any> => {
  const { hotel_id, start_date, end_date } = userData;
  const queryString = `hotel_id=${hotel_id}&start_date=${start_date}&end_date=${end_date}`;
  return apiRequest(`/spaAppointment/export?${queryString}`, {}, "get"); // ← {} not null, GET not POST
};

export const getReservationsByDateRange = (userData: any): Promise<any> => {
  return apiRequest(`/reservations/by-date-range`, userData, "post");
};

// Forgot Password APIs
export const requestPasswordResetUserAPI = (email: string): Promise<any> => {
  return apiRequest("/forgot-password/user/request-otp", { email }, "post");
};

export const verifyOTPUserAPI = (email: string, otp: string): Promise<any> => {
  return apiRequest("/forgot-password/user/verify-otp", { email, otp }, "post");
};

export const resetPasswordUserAPI = (email: string, password: string, confirmPassword: string): Promise<any> => {
  return apiRequest("/forgot-password/user/reset-password", { email, password, confirmPassword }, "post");
};

// Employee Forgot Password APIs
export const requestPasswordResetEmployeeAPI = (email: string): Promise<any> => {
  return apiRequest("/forgot-password/employee/request-otp", { email }, "post");
};

export const verifyOTPEmployeeAPI = (email: string, otp: string): Promise<any> => {
  return apiRequest("/forgot-password/employee/verify-otp", { email, otp }, "post");
};

export const resetPasswordEmployeeAPI = (email: string, password: string, confirmPassword: string): Promise<any> => {
  return apiRequest("/forgot-password/employee/reset-password", { email, password, confirmPassword }, "post");
};
